import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Settings, 
  Link as LinkIcon, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  Database,
  Info,
  Clock,
  AlertCircle,
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Package,
  ShoppingCart,
  Building2,
  TrendingUp,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

interface SyncRecord {
  id: string;
  timestamp: Date;
  entityType: "Stock Items" | "Stock Movements" | "Purchase Orders" | "Suppliers" | "Warehouses";
  operation: "Create" | "Update" | "Delete";
  status: "success" | "failed" | "pending" | "partial";
  recordsProcessed: number;
  recordsTotal: number;
  source: "Access Financials" | "Access Dimensions";
  duration: number;
  errorMessage?: string;
  reference?: string;
}

type SortColumn = "timestamp" | "entityType" | "status" | "recordsProcessed" | "duration";
type SortDirection = "asc" | "desc";

// Generate mock sync records
const generateMockSyncRecords = (): SyncRecord[] => {
  const records: SyncRecord[] = [];
  const now = new Date();
  
  const entityTypes: SyncRecord["entityType"][] = [
    "Stock Items",
    "Stock Movements",
    "Purchase Orders",
    "Suppliers",
    "Warehouses",
  ];
  
  const operations: SyncRecord["operation"][] = ["Create", "Update", "Delete"];
  const statuses: SyncRecord["status"][] = ["success", "success", "success", "success", "failed", "partial"];
  const sources: SyncRecord["source"][] = ["Access Financials", "Access Financials", "Access Financials"];
  
  for (let i = 0; i < 50; i++) {
    const daysAgo = Math.floor(Math.random() * 7);
    const hoursAgo = Math.floor(Math.random() * 24);
    const minutesAgo = Math.floor(Math.random() * 60);
    
    const timestamp = new Date(now);
    timestamp.setDate(timestamp.getDate() - daysAgo);
    timestamp.setHours(timestamp.getHours() - hoursAgo);
    timestamp.setMinutes(timestamp.getMinutes() - minutesAgo);
    
    const entityType = entityTypes[Math.floor(Math.random() * entityTypes.length)];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const source = sources[Math.floor(Math.random() * sources.length)];
    
    let recordsTotal = 0;
    let recordsProcessed = 0;
    
    switch (entityType) {
      case "Stock Items":
        recordsTotal = Math.floor(Math.random() * 500) + 100;
        break;
      case "Stock Movements":
        recordsTotal = Math.floor(Math.random() * 200) + 50;
        break;
      case "Purchase Orders":
        recordsTotal = Math.floor(Math.random() * 50) + 10;
        break;
      case "Suppliers":
        recordsTotal = Math.floor(Math.random() * 30) + 5;
        break;
      case "Warehouses":
        recordsTotal = Math.floor(Math.random() * 10) + 1;
        break;
    }
    
    if (status === "success") {
      recordsProcessed = recordsTotal;
    } else if (status === "partial") {
      recordsProcessed = Math.floor(recordsTotal * (0.5 + Math.random() * 0.4));
    } else if (status === "failed") {
      recordsProcessed = 0;
    } else {
      recordsProcessed = Math.floor(recordsTotal * Math.random());
    }
    
    const duration = Math.floor(Math.random() * 120) + 5;
    
    records.push({
      id: `SYNC-${String(i + 1).padStart(4, "0")}`,
      timestamp,
      entityType,
      operation,
      status,
      recordsProcessed,
      recordsTotal,
      source,
      duration,
      errorMessage: status === "failed" ? "Connection timeout to ERP system" : 
                    status === "partial" ? "Some records failed validation" : undefined,
      reference: `REF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
    });
  }
  
  return records.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

export function IntegrationSyncPage() {
  const [activeTab, setActiveTab] = useState("connection");
  const [financialsEnabled, setFinancialsEnabled] = useState(false);
  const [dimensionsEnabled, setDimensionsEnabled] = useState(false);
  const [financialsDatabase, setFinancialsDatabase] = useState("");
  const [dimensionsDatabase, setDimensionsDatabase] = useState("");
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [moduleToDisable, setModuleToDisable] = useState<"financials" | "dimensions" | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Sync Status state
  const [syncRecords] = useState<SyncRecord[]>(generateMockSyncRecords());
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [sortColumn, setSortColumn] = useState<SortColumn>("timestamp");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Load settings from localStorage on mount
  useEffect(() => {
    const financials = localStorage.getItem("integration.financials.enabled") === "true";
    const dimensions = localStorage.getItem("integration.dimensions.enabled") === "true";
    const financialsDb = localStorage.getItem("integration.financials.database") || "";
    const dimensionsDb = localStorage.getItem("integration.dimensions.database") || "";
    setFinancialsEnabled(financials);
    setDimensionsEnabled(dimensions);
    setFinancialsDatabase(financialsDb);
    setDimensionsDatabase(dimensionsDb);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    localStorage.setItem("integration.financials.enabled", financialsEnabled.toString());
    localStorage.setItem("integration.dimensions.enabled", dimensionsEnabled.toString());
    localStorage.setItem("integration.financials.database", financialsDatabase);
    localStorage.setItem("integration.dimensions.database", dimensionsDatabase);
    setIsSaving(false);
    toast.success("Integration settings saved successfully");
  };

  const handleTestConnection = async (module: "financials" | "dimensions") => {
    toast.info(`Testing connection to ${module}...`);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    toast.success(`${module} connection test successful`);
  };

  const handleManualSync = async (module: "financials" | "dimensions") => {
    const moduleName = module === "financials" ? "Access Financials" : "Access Dimensions";
    toast.info(`Starting manual sync for ${moduleName}...`);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    toast.success(`${moduleName} sync completed successfully`);
  };

  const handleToggleIntegration = (module: "financials" | "dimensions", currentState: boolean) => {
    // If trying to disable (currentState is true), show confirmation dialog
    if (currentState) {
      setModuleToDisable(module);
      setShowDisableDialog(true);
    } else {
      // If enabling, just enable it
      if (module === "financials") {
        setFinancialsEnabled(true);
      } else {
        setDimensionsEnabled(true);
      }
    }
  };

  const confirmDisableIntegration = () => {
    if (moduleToDisable === "financials") {
      setFinancialsEnabled(false);
      setFinancialsDatabase("");
      localStorage.removeItem("financialsDatabase");
      toast.success("Access Financials integration disabled and disconnected");
    } else if (moduleToDisable === "dimensions") {
      setDimensionsEnabled(false);
      setDimensionsDatabase("");
      localStorage.removeItem("dimensionsDatabase");
      toast.success("Access Dimensions integration disabled and disconnected");
    }
    setShowDisableDialog(false);
    setModuleToDisable(null);
  };

  const cancelDisableIntegration = () => {
    setShowDisableDialog(false);
    setModuleToDisable(null);
  };

  // Filter and sort records
  const filteredRecords = useMemo(() => {
    let filtered = syncRecords;

    if (searchTerm) {
      filtered = filtered.filter(
        (record) =>
          record.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.entityType.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.reference?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((record) => record.status === statusFilter);
    }

    if (entityFilter !== "all") {
      filtered = filtered.filter((record) => record.entityType === entityFilter);
    }

    filtered.sort((a, b) => {
      let aValue: any = a[sortColumn];
      let bValue: any = b[sortColumn];

      if (sortColumn === "timestamp") {
        aValue = a.timestamp.getTime();
        bValue = b.timestamp.getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [syncRecords, searchTerm, statusFilter, entityFilter, sortColumn, sortDirection]);

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status: SyncRecord["status"]) => {
    switch (status) {
      case "success":
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Success
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case "partial":
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Partial
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Integration & Sync</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Configure integrations with external systems and monitor synchronisation status
        </p>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Management</CardTitle>
          <CardDescription>
            Connect to ERP systems and monitor data synchronisation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="connection">Connection Settings</TabsTrigger>
              <TabsTrigger value="sync">Sync History & Status</TabsTrigger>
            </TabsList>

            {/* Connection Settings Tab */}
            <TabsContent value="connection" className="space-y-6 mt-6">
              {/* Financials Integration */}
              <Card className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Database className="h-5 w-5 text-primary" />
                        <h3 className="text-xl font-semibold">Access Financials</h3>
                        {financialsEnabled ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disconnected
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Connect to your ERP's Financials module to sync GL accounts for stock item analysis codes
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Switch
                          id="financials-enabled"
                          checked={financialsEnabled}
                          onCheckedChange={() => handleToggleIntegration("financials", financialsEnabled)}
                        />
                        <div>
                          <Label htmlFor="financials-enabled" className="cursor-pointer">
                            Enable Financials Integration
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            Sync stock-related data including Sales Orders, Purchase Orders, Customers, Suppliers, and GL Accounts
                          </p>
                        </div>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              When enabled, stock-related data including Sales Orders, Purchase Orders, Customers, Suppliers, and GL Accounts will be synced from your ERP
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    {financialsEnabled && (
                      <div className="space-y-3 pl-4 border-l-2 border-primary">
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="financials-database" className="text-sm font-medium mb-2 block">
                              Database Instance
                            </Label>
                            <Select value={financialsDatabase} onValueChange={setFinancialsDatabase}>
                              <SelectTrigger id="financials-database">
                                <SelectValue placeholder="Select a database instance" />
                              </SelectTrigger>
                              <SelectContent searchable={false}>
                                <SelectItem value="database-1">Database 1</SelectItem>
                                <SelectItem value="database-2">Database 2</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {financialsDatabase && (
                          <>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <Label className="text-xs text-muted-foreground">ERP System</Label>
                                <p className="font-medium">Access Financials</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Last Sync</Label>
                                <p className="font-medium">{new Date().toLocaleString()}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Data Synced</Label>
                                <p className="font-medium">All stock data synced</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Status</Label>
                                <p className="font-medium text-green-600">Active</p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => handleTestConnection("financials")}
                              >
                                <RefreshCw className="h-4 w-4" />
                                Test Connection
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                className="gap-2"
                                onClick={() => handleManualSync("financials")}
                              >
                                <RefreshCw className="h-4 w-4" />
                                Sync Now
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Dimensions Integration */}
              <Card className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <LinkIcon className="h-5 w-5 text-primary" />
                        <h3 className="text-xl font-semibold">Access Dimensions</h3>
                        {dimensionsEnabled ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disconnected
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Connect to your ERP's Dimensions module for advanced analysis and reporting
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Switch
                          id="dimensions-enabled"
                          checked={dimensionsEnabled}
                          onCheckedChange={() => handleToggleIntegration("dimensions", dimensionsEnabled)}
                        />
                        <div>
                          <Label htmlFor="dimensions-enabled" className="cursor-pointer">
                            Enable Dimensions Integration
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            Sync dimension data for advanced reporting
                          </p>
                        </div>
                      </div>
                    </div>

                    {dimensionsEnabled && (
                      <div className="space-y-3 pl-4 border-l-2 border-primary">
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="dimensions-database" className="text-sm font-medium mb-2 block">
                              Database Instance
                            </Label>
                            <Select value={dimensionsDatabase} onValueChange={setDimensionsDatabase}>
                              <SelectTrigger id="dimensions-database">
                                <SelectValue placeholder="Select a database instance" />
                              </SelectTrigger>
                              <SelectContent searchable={false}>
                                <SelectItem value="database-1">Database 1</SelectItem>
                                <SelectItem value="database-2">Database 2</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {dimensionsDatabase && (
                          <>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <Label className="text-xs text-muted-foreground">ERP System</Label>
                                <p className="font-medium">Access Dimensions</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Last Sync</Label>
                                <p className="font-medium">{new Date().toLocaleString()}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Dimensions</Label>
                                <p className="font-medium">12 dimensions synced</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Status</Label>
                                <p className="font-medium text-green-600">Active</p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => handleTestConnection("dimensions")}
                              >
                                <RefreshCw className="h-4 w-4" />
                                Test Connection
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                className="gap-2"
                                onClick={() => handleManualSync("dimensions")}
                              >
                                <RefreshCw className="h-4 w-4" />
                                Sync Now
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={isSaving}>
                  <Settings className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </TabsContent>

            {/* Sync History & Status Tab */}
            <TabsContent value="sync" className="space-y-6 mt-6">
              {/* Filters and Search */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by ID, entity type, or reference..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by entity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entities</SelectItem>
                    <SelectItem value="Stock Items">Stock Items</SelectItem>
                    <SelectItem value="Stock Movements">Stock Movements</SelectItem>
                    <SelectItem value="Purchase Orders">Purchase Orders</SelectItem>
                    <SelectItem value="Suppliers">Suppliers</SelectItem>
                    <SelectItem value="Warehouses">Warehouses</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sync Records Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sync ID</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Entity Type</TableHead>
                      <TableHead>Operation</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Records</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-mono text-sm">{record.id}</TableCell>
                        <TableCell className="text-sm">
                          {record.timestamp.toLocaleString()}
                        </TableCell>
                        <TableCell>{record.entityType}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{record.operation}</Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="text-sm">
                              {record.recordsProcessed} / {record.recordsTotal}
                            </span>
                            <Progress
                              value={(record.recordsProcessed / record.recordsTotal) * 100}
                              className="h-1"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{record.duration}s</TableCell>
                        <TableCell className="text-sm">{record.source}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredRecords.length)} of{" "}
                  {filteredRecords.length} records
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Disable Integration Confirmation Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable the{" "}
              <strong>
                {moduleToDisable === "financials" ? "Access Financials" : "Access Dimensions"}
              </strong>{" "}
              integration? This will disconnect from{" "}
              <strong>
                {moduleToDisable === "financials" ? financialsDatabase : dimensionsDatabase}
              </strong>{" "}
              and you will need to reconfigure the integration to re-enable it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDisableIntegration}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisableIntegration} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Disable Integration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
